import { useNodeManager } from '@/composables/useNodeManager';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { FrustumLight } from '@/threejs/shaders/projection/frustum-light';
import * as THREE from 'three';
import { CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import { renderer } from './renderer';
// Import selection store to check if target is selected and mode is translate
import { useSelectionStore } from '@/stores/ui/selection';
// State
let helperLinesEnabled = true;
let projectors = [];
let projectorHelperLines = [];

// References set during init
let _scene = null;
let _gizmoScene = null;
let _gizmoLayer = 1;
let _camera = null;

// Lazy-init node manager (needs Pinia to be ready)
let _nodeManager = null;
function getNodeManager() {
  if (!_nodeManager) {
    _nodeManager = useNodeManager();
  }
  return _nodeManager;
}

// Drag state
let dragState = null;
let hoveredEdge = null;

// Reusable objects for helper line calculations
const helperRaycaster = new THREE.Raycaster();
helperRaycaster.layers.set(0);
const helperCorners = [
  new THREE.Vector2(-1, -1),
  new THREE.Vector2(1, -1),
  new THREE.Vector2(1, 1),
  new THREE.Vector2(-1, 1),
];
const helperOrigin = new THREE.Vector3();
const helperDefaultColor = new THREE.Color(0x86e6ff);
const helperPlane = new THREE.Plane();
const helperPlaneTarget = new THREE.Vector3();
const helperFrameCorners = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];

// Reusable objects for drag calculations
const dragRaycaster = new THREE.Raycaster();
const dragPlane = new THREE.Plane();
const dragIntersection = new THREE.Vector3();
const dragProjectorOrigin = new THREE.Vector3();
const dragUpVector = new THREE.Vector3();
const dragRightVector = new THREE.Vector3();
const dragPlaneCenter = new THREE.Vector3();
const dragPointer = new THREE.Vector2();

// Reusable vector for screen projection
const screenProjection = new THREE.Vector3();

const THROW_RATIO_MIN = 0.3;
const THROW_RATIO_MAX = 10.0;
const EDGE_PROXIMITY_PX = 10;

// Edge definitions: [cornerA, cornerB, type]
// Corners: 0=bottom-left, 1=bottom-right, 2=top-right, 3=top-left
const EDGES = [
  [0, 1, 'horizontal'], // bottom
  [1, 2, 'vertical'], // right
  [2, 3, 'horizontal'], // top
  [3, 0, 'vertical'], // left
];

export function initProjectorHelperLines(scene, gizmoScene, gizmoLayer, camera) {
  _scene = scene;
  _gizmoScene = gizmoScene;
  _gizmoLayer = gizmoLayer;
  _camera = camera;

  // Canvas-level listeners for edge dragging
  renderer.domElement.addEventListener('pointerdown', onCanvasPointerDown, {
    capture: true,
  });
  renderer.domElement.addEventListener('pointermove', onCanvasHover);
}

export function getProjectors() {
  return projectors;
}

// --- Screen-space edge proximity detection ---

function projectToScreen(worldPos, rect) {
  screenProjection.copy(worldPos).project(_camera);
  return {
    x: (screenProjection.x * 0.5 + 0.5) * rect.width,
    y: (-screenProjection.y * 0.5 + 0.5) * rect.height,
  };
}

function pointToSegmentDistanceSq(px, py, ax, ay, bx, by) {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const ex = px - ax;
    const ey = py - ay;
    return ex * ex + ey * ey;
  }
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  const closestX = ax + t * dx;
  const closestY = ay + t * dy;
  const ex = px - closestX;
  const ey = py - closestY;
  return ex * ex + ey * ey;
}

function findNearestEdge(clientX, clientY) {
  if (!_camera) return null;
  const rect = renderer.domElement.getBoundingClientRect();
  const px = clientX - rect.left;
  const py = clientY - rect.top;

  const thresholdSq = EDGE_PROXIMITY_PX * EDGE_PROXIMITY_PX;
  let bestDistSq = thresholdSq;
  let bestResult = null;

  for (let i = 0; i < projectors.length; i++) {
    const helper = projectorHelperLines[i];
    if (!helper || !helper.line.visible) continue;
    // Only allow dragging edges of selected projectors
    if (!projectors[i].userData.selected) continue;

    for (const [a, b, edgeType] of EDGES) {
      const sa = projectToScreen(helper.frameCorners[a], rect);
      const sb = projectToScreen(helper.frameCorners[b], rect);
      const distSq = pointToSegmentDistanceSq(px, py, sa.x, sa.y, sb.x, sb.y);
      if (distSq < bestDistSq) {
        bestDistSq = distSq;
        bestResult = {
          projectorIndex: i,
          projector: projectors[i],
          edgeType,
        };
      }
    }
  }

  return bestResult;
}

// --- Canvas event handlers ---

function onCanvasHover(e) {
  // Don't update hover cursor during drag or when buttons are pressed
  if (dragState || e.buttons !== 0) return;

  // Disable frustum edge hover if projector target is selected and mode is translate
  const selectionStore = useSelectionStore();
  if (selectionStore.selectedObjectTransformTarget && selectionStore.selectedObjectTransformType === 'translate') {
    return;
  }

  const result = findNearestEdge(e.clientX, e.clientY);
  if (result) {
    hoveredEdge = result;
    renderer.domElement.style.cursor = result.edgeType === 'horizontal' ? 'ns-resize' : 'ew-resize';
  } else if (hoveredEdge) {
    hoveredEdge = null;
    renderer.domElement.style.cursor = '';
  }
}

function onCanvasPointerDown(e) {
  // Only handle left mouse button
  if (e.button !== 0) return;

  // Disable frustum edge drag if projector target is selected and mode is translate
  const selectionStore = useSelectionStore();
  if (selectionStore.selectedObjectTransformTarget && selectionStore.selectedObjectTransformType === 'translate') {
    return;
  }

  const result = findNearestEdge(e.clientX, e.clientY);
  if (!result) return;

  // Prevent camera controls from picking up this drag
  e.stopImmediatePropagation();
  e.preventDefault();

  startDrag(result.projector, result.edgeType);
}

// --- Drag interaction ---

function startDrag(projector, edgeType) {
  if (!_camera) return;

  const cruxModuleStore = useCruxModuleStore();
  cruxModuleStore.pause();

  projector.updateMatrixWorld(true);
  projector.camera.updateMatrixWorld(true);
  projector.camera.getWorldPosition(dragProjectorOrigin);

  const targetDistance = Math.max(dragProjectorOrigin.distanceTo(projector.userData.target), 0.05);

  // Build the target plane (perpendicular to projector forward, at target distance)
  const forward = projector.camera.getWorldDirection(new THREE.Vector3());
  dragPlaneCenter.copy(forward).multiplyScalar(targetDistance).add(dragProjectorOrigin);
  dragPlane.setFromNormalAndCoplanarPoint(forward, dragPlaneCenter);

  // Get the projector camera's world-space up and right vectors
  const quat = projector.camera.getWorldQuaternion(new THREE.Quaternion());
  dragUpVector.set(0, 1, 0).applyQuaternion(quat);
  dragRightVector.set(1, 0, 0).applyQuaternion(quat);

  dragState = {
    projector,
    targetDistance,
    aspect: projector.userData.aspect,
    edgeType,
  };

  window.addEventListener('pointermove', onDragMove);
  window.addEventListener('pointerup', onDragEnd);
}

function onDragMove(e) {
  if (!dragState || !_camera) return;

  const rect = renderer.domElement.getBoundingClientRect();

  // Normalize pointer to [-1, 1]
  dragPointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
  dragPointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

  dragRaycaster.setFromCamera(dragPointer, _camera);
  const hit = dragRaycaster.ray.intersectPlane(dragPlane, dragIntersection);
  if (!hit) return;

  const offset = dragIntersection.clone().sub(dragPlaneCenter);
  const { targetDistance, aspect, projector, edgeType } = dragState;

  let newThrowRatio;
  if (edgeType === 'horizontal') {
    // Top or bottom edge: measure half-height along projector up axis
    const halfHeight = Math.abs(offset.dot(dragUpVector));
    if (halfHeight < 0.01) return;
    newThrowRatio = targetDistance / (2 * aspect * halfHeight);
  } else {
    // Left or right edge: measure half-width along projector right axis
    const halfWidth = Math.abs(offset.dot(dragRightVector));
    if (halfWidth < 0.01) return;
    newThrowRatio = targetDistance / (2 * halfWidth);
  }

  newThrowRatio = Math.max(THROW_RATIO_MIN, Math.min(THROW_RATIO_MAX, newThrowRatio));

  projector.userData.throwRatio = newThrowRatio;
  projector.updateCamera();

  const { updateNode } = getNodeManager();
  updateNode(projector.userData.code, {
    throwRatio: projector.userData.throwRatio,
  });

  window.dispatchEvent(new Event('render'));
}

function onDragEnd() {
  if (dragState) {
    const cruxModuleStore = useCruxModuleStore();
    cruxModuleStore.resume();
    cruxModuleStore.commit();
    const { projector } = dragState;
    const { updateNode } = getNodeManager();
    updateNode(projector.userData.code, { throwRatio: projector.userData.throwRatio }, true);
  }
  dragState = null;
  renderer.domElement.style.cursor = '';
  window.removeEventListener('pointermove', onDragMove);
  window.removeEventListener('pointerup', onDragEnd);
}

// --- Label creation ---

function createLabel(rotated = false) {
  const el = document.createElement('div');
  el.style.color = 'white';
  el.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
  el.style.padding = '2px 6px';
  el.style.borderRadius = '4px';
  el.style.fontSize = '10px';
  el.style.fontFamily = 'Arial, sans-serif';
  el.style.pointerEvents = 'none';
  el.style.whiteSpace = 'nowrap';
  if (rotated) {
    el.style.transform = 'rotate(-90deg)';
  }
  const obj = new CSS2DObject(el);
  obj.visible = false;
  obj.userData.projectorOverlayIgnore = true;
  _scene.add(obj);
  return { el, obj };
}

// Reusable vectors for label positioning
const labelMidpoint = new THREE.Vector3();

// --- Projector helper line creation ---

const createProjectorHelperLines = (projector) => {
  // 4 corner lines (4 * 2 * 3) + 4 frame edges (4 * 2 * 3) = 48 floats
  const positions = new Float32Array(8 * 2 * 3);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const edgeColor = projector.userData.edgeColor;
  const material = new THREE.LineBasicMaterial({
    color: edgeColor ?? helperDefaultColor,
    transparent: true,
    opacity: 0.05,
  });
  material.depthTest = false;
  material.depthWrite = false;
  const line = new THREE.LineSegments(geometry, material);
  line.frustumCulled = false;
  line.renderOrder = 999;
  line.layers.set(_gizmoLayer);
  line.visible = helperLinesEnabled;
  line.userData.projectorOverlayIgnore = true;
  _gizmoScene.add(line);

  // Per-helper stored frame corners for hit testing
  const frameCorners = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];

  // Semi-transparent frustum side faces
  const frustumLight = new FrustumLight(_gizmoScene, _gizmoLayer, {
    color: edgeColor ?? helperDefaultColor,
  });
  frustumLight.setVisible(helperLinesEnabled);

  // Labels: throw ratio (top-left corner), width (top edge), height (left edge)
  const throwRatioLabel = createLabel();
  const widthLabel = createLabel();
  const heightLabel = createLabel(true);

  return {
    line,
    positions,
    material,
    frameCorners,
    frustumLight,
    throwRatioLabel,
    widthLabel,
    heightLabel,
  };
};

export function addProjector(projector) {
  if (projectors.includes(projector)) return;
  projectors.push(projector);
  projectorHelperLines.push(createProjectorHelperLines(projector));
}

export function removeProjector(projector) {
  let index = projectors.indexOf(projector);
  if (index === -1) {
    // Fallback: search by code in case the object reference differs (e.g. during deletion)
    const code = projector.userData?.code;
    if (code) index = projectors.findIndex((p) => p.userData?.code === code);
  }
  if (index === -1) return;
  projectors.splice(index, 1);
  const helper = projectorHelperLines.splice(index, 1)[0];
  if (helper) {
    _gizmoScene.remove(helper.line);
    helper.line.geometry.dispose();
    helper.material.dispose();
    helper.frustumLight.dispose();
    _scene.remove(helper.throwRatioLabel.obj);
    _scene.remove(helper.widthLabel.obj);
    _scene.remove(helper.heightLabel.obj);
  }
}

export const setHelperLinesEnabled = (enabled) => {
  helperLinesEnabled = enabled;
  for (const helper of projectorHelperLines) {
    helper.line.visible = enabled;
    helper.frustumLight.setVisible(enabled);
  }
};

export const updateProjectorHelperLines = () => {
  if (!helperLinesEnabled) {
    return;
  }

  for (let i = 0; i < projectors.length; i += 1) {
    const projector = projectors[i];
    const helper = projectorHelperLines[i];
    if (!helper || !projector) {
      continue;
    }

    helper.line.visible = true;
    projector.updateMatrixWorld(true);
    projector.camera.updateMatrixWorld(true);
    projector.camera.getWorldPosition(helperOrigin);
    const targetDistance = Math.max(helperOrigin.distanceTo(projector.userData.target), 0.05);

    const edgeColor = projector.userData.edgeColor;
    helper.material.color.copy(edgeColor ?? helperDefaultColor);
    helper.material.opacity = Number(projector.userData.power_brightness) / 600 ?? 0.05;

    // Get camera forward direction for plane intersection
    projector.camera.getWorldDirection(helperPlaneTarget);
    // Create a plane at targetDistance from the camera, perpendicular to camera direction
    helperPlaneTarget.multiplyScalar(targetDistance).add(helperOrigin);
    helperPlane.setFromNormalAndCoplanarPoint(
      projector.camera.getWorldDirection(new THREE.Vector3()),
      helperPlaneTarget
    );

    for (let cornerIndex = 0; cornerIndex < helperCorners.length; cornerIndex += 1) {
      const corner = helperCorners[cornerIndex];
      helperRaycaster.setFromCamera({ x: corner.x, y: corner.y }, projector.camera);

      // Calculate frame corner at target plane distance
      helperRaycaster.ray.intersectPlane(helperPlane, helperFrameCorners[cornerIndex]);
      // Fallback if ray is parallel to plane
      if (helperFrameCorners[cornerIndex].lengthSq() === 0) {
        helperFrameCorners[cornerIndex]
          .copy(helperRaycaster.ray.direction)
          .multiplyScalar(targetDistance)
          .add(helperRaycaster.ray.origin);
      }

      // Corner lines always extend to the target plane
      const offset = cornerIndex * 6;
      helper.positions[offset] = helperOrigin.x;
      helper.positions[offset + 1] = helperOrigin.y;
      helper.positions[offset + 2] = helperOrigin.z;
      helper.positions[offset + 3] = helperFrameCorners[cornerIndex].x;
      helper.positions[offset + 4] = helperFrameCorners[cornerIndex].y;
      helper.positions[offset + 5] = helperFrameCorners[cornerIndex].z;
    }

    // Store frame corners for this helper (for edge hit testing)
    for (let j = 0; j < 4; j++) {
      helper.frameCorners[j].copy(helperFrameCorners[j]);
    }

    // Draw frame rectangle at target distance
    // Frame edges start at offset 24 (after 4 corner lines)
    // Corners: 0=bottom-left, 1=bottom-right, 2=top-right, 3=top-left
    const frameOffset = 24;
    // Bottom edge: 0 -> 1
    helper.positions[frameOffset] = helperFrameCorners[0].x;
    helper.positions[frameOffset + 1] = helperFrameCorners[0].y;
    helper.positions[frameOffset + 2] = helperFrameCorners[0].z;
    helper.positions[frameOffset + 3] = helperFrameCorners[1].x;
    helper.positions[frameOffset + 4] = helperFrameCorners[1].y;
    helper.positions[frameOffset + 5] = helperFrameCorners[1].z;
    // Right edge: 1 -> 2
    helper.positions[frameOffset + 6] = helperFrameCorners[1].x;
    helper.positions[frameOffset + 7] = helperFrameCorners[1].y;
    helper.positions[frameOffset + 8] = helperFrameCorners[1].z;
    helper.positions[frameOffset + 9] = helperFrameCorners[2].x;
    helper.positions[frameOffset + 10] = helperFrameCorners[2].y;
    helper.positions[frameOffset + 11] = helperFrameCorners[2].z;
    // Top edge: 2 -> 3
    helper.positions[frameOffset + 12] = helperFrameCorners[2].x;
    helper.positions[frameOffset + 13] = helperFrameCorners[2].y;
    helper.positions[frameOffset + 14] = helperFrameCorners[2].z;
    helper.positions[frameOffset + 15] = helperFrameCorners[3].x;
    helper.positions[frameOffset + 16] = helperFrameCorners[3].y;
    helper.positions[frameOffset + 17] = helperFrameCorners[3].z;
    // Left edge: 3 -> 0
    helper.positions[frameOffset + 18] = helperFrameCorners[3].x;
    helper.positions[frameOffset + 19] = helperFrameCorners[3].y;
    helper.positions[frameOffset + 20] = helperFrameCorners[3].z;
    helper.positions[frameOffset + 21] = helperFrameCorners[0].x;
    helper.positions[frameOffset + 22] = helperFrameCorners[0].y;
    helper.positions[frameOffset + 23] = helperFrameCorners[0].z;

    helper.line.geometry.attributes.position.needsUpdate = true;

    // Update frustum side faces
    helper.frustumLight.update(helperOrigin, helperFrameCorners);
    helper.frustumLight.setVisible(helperLinesEnabled);
    helper.frustumLight.setColor(edgeColor ?? helperDefaultColor);
    helper.frustumLight.setOpacity(Number(projector.userData.power_brightness) / 1000 ?? 0);

    // Update labels (only visible when projector is selected)
    const isSelected = projector.userData.selected || false;
    helper.throwRatioLabel.obj.visible = isSelected && helperLinesEnabled;
    helper.widthLabel.obj.visible = isSelected && helperLinesEnabled;
    helper.heightLabel.obj.visible = isSelected && helperLinesEnabled;
    if (isSelected) {
      const width = helperFrameCorners[0].distanceTo(helperFrameCorners[1]);
      const height = helperFrameCorners[0].distanceTo(helperFrameCorners[3]);

      // Throw ratio label at top-left corner (corner 3)
      helper.throwRatioLabel.el.textContent = `tr: ${projector.userData.throwRatio.toFixed(2)}`;
      helper.throwRatioLabel.obj.position.copy(helperFrameCorners[3]);

      // Width label at midpoint of top edge (corners 2 → 3)
      labelMidpoint.addVectors(helperFrameCorners[2], helperFrameCorners[3]).multiplyScalar(0.5);
      helper.widthLabel.el.textContent = `${width.toFixed(2)}m`;
      helper.widthLabel.obj.position.copy(labelMidpoint);

      // Height label at midpoint of left edge (corners 3 → 0)
      labelMidpoint.addVectors(helperFrameCorners[3], helperFrameCorners[0]).multiplyScalar(0.5);
      helper.heightLabel.el.textContent = `${height.toFixed(2)}m`;
      helper.heightLabel.obj.position.copy(labelMidpoint);
    }
  }
};

export function cleanupProjectorHelperLines() {
  // Remove canvas listeners
  renderer.domElement.removeEventListener('pointerdown', onCanvasPointerDown, {
    capture: true,
  });
  renderer.domElement.removeEventListener('pointermove', onCanvasHover);

  // Remove any active drag listeners
  window.removeEventListener('pointermove', onDragMove);
  window.removeEventListener('pointerup', onDragEnd);
  dragState = null;
  hoveredEdge = null;

  for (let i = 0; i < projectorHelperLines.length; i++) {
    const helper = projectorHelperLines[i];
    projectors[i]?.dispose();
    if (helper.line) {
      _gizmoScene.remove(helper.line);
      helper.line.geometry.dispose();
      helper.material.dispose();
    }
    helper.frustumLight?.dispose();
    _scene.remove(helper.throwRatioLabel.obj);
    _scene.remove(helper.widthLabel.obj);
    _scene.remove(helper.heightLabel.obj);
  }
  projectorHelperLines = [];
  projectors = [];
  helperLinesEnabled = true;
}
